import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  ChartOptions,
  ChartData,
  Plugin,
} from 'chart.js';
import { FormatToShortPriceTemplate } from '@utils/FormatValue';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Filler, Tooltip);

interface GaussBellCurveProps {
  sigma: number;        // annualized volatility (e.g. 0.35)
  mu: number;           // mean annual return (e.g. -0.06)
  exposure: number;     // net exposure in $
  percentile: number;   // selected percentile (5, 10, 25)
  width?: number;
  height?: number;
}

// Normal PDF
const normalPdf = (x: number, mu: number, sigma: number): number => {
  const coeff = 1 / (sigma * Math.sqrt(2 * Math.PI));
  const exponent = -((x - mu) ** 2) / (2 * sigma ** 2);
  return coeff * Math.exp(exponent);
};

// Z-scores for percentiles
const zScores: Record<number, number> = {
  5: 1.645,
  10: 1.282,
  25: 0.674,
};

const NUM_POINTS = 200;

export const GaussBellCurve: React.FC<GaussBellCurveProps> = ({
  sigma,
  mu,
  exposure,
  percentile,
  width = 180,
  height = 80,
}) => {
  const zScore = zScores[percentile] || 1.645;
  const varThreshold = mu - zScore * sigma;

  // Keep refs so the plugin + tooltip always read the latest values without re-mount
  const varRef = React.useRef(varThreshold);
  const exposureRef = React.useRef(exposure);
  varRef.current = varThreshold;
  exposureRef.current = exposure;

  const chartRef = React.useRef<ChartJS<'line'>>(null);

  // When percentile changes, just redraw the chart so the plugin picks up the new varRef
  React.useEffect(() => {
    chartRef.current?.draw();
  }, [percentile]);

  const xMin = mu - 3.5 * sigma;
  const xMax = mu + 3.5 * sigma;

  // Stable refs for plugin to read sigma/mu without triggering data change
  const sigmaRef = React.useRef(sigma);
  const muRef = React.useRef(mu);
  sigmaRef.current = sigma;
  muRef.current = mu;

  // Chart data: only the main bell curve (stable when percentile changes)
  const { xValues, yValues } = React.useMemo(() => {
    const step = (xMax - xMin) / NUM_POINTS;
    const xs: number[] = [], ys: number[] = [];
    for (let i = 0; i <= NUM_POINTS; i++) {
      const x = xMin + i * step;
      xs.push(x);
      ys.push(normalPdf(x, mu, sigma));
    }
    return { xValues: xs, yValues: ys };
  }, [sigma, mu, xMin, xMax]);

  const data: ChartData<'line'> = React.useMemo(() => ({
    labels: xValues,
    datasets: [
      {
        data: yValues,
        fill: false,
        borderColor: '#6b7280',
        borderWidth: 1.5,
        pointRadius: 0,
        pointHitRadius: 8,
        tension: 0.4,
      },
    ],
  }), [xValues, yValues]);

  // Plugin draws tail shading + dashed line via canvas (reads from refs, no chart data change)
  const varOverlayPlugin = React.useMemo<Plugin<'line'>>(() => ({
    id: 'varOverlay',
    afterDatasetsDraw(chart) {
      const { ctx, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;
      if (!xScale || !yScale) return;

      const vt = varRef.current;
      const s = sigmaRef.current;
      const m = muRef.current;
      const yMax = yScale.max;

      // Draw shaded tail region
      ctx.save();
      ctx.beginPath();
      const xStart = xScale.left;
      const vtPixel = xScale.getPixelForValue(vt);
      ctx.moveTo(xStart, yScale.bottom);
      // Trace the PDF curve up to varThreshold
      for (let px = xStart; px <= vtPixel; px += 1) {
        const xVal = xScale.getValueForPixel(px) ?? 0;
        const yVal = normalPdf(xVal, m, s);
        const yPx = yScale.getPixelForValue(yVal);
        ctx.lineTo(px, yPx);
      }
      ctx.lineTo(vtPixel, yScale.bottom);
      ctx.closePath();
      ctx.fillStyle = 'rgba(239, 68, 68, 0.25)';
      ctx.fill();

      // Draw dashed VaR line
      ctx.beginPath();
      ctx.setLineDash([3, 3]);
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 1;
      ctx.moveTo(vtPixel, yScale.top);
      ctx.lineTo(vtPixel, yScale.bottom);
      ctx.stroke();
      ctx.restore();
    },
  }), []);

  const options: ChartOptions<'line'> = React.useMemo(() => ({
    responsive: false,
    maintainAspectRatio: false,
    animation: false,
    layout: { padding: { top: 2, bottom: 0, left: 0, right: 0 } },
    scales: {
      x: { type: 'linear' as const, display: false, min: xMin, max: xMax },
      y: { display: false, min: 0 },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        displayColors: false,
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleFont: { size: 10 },
        bodyFont: { size: 10 },
        padding: 6,
        callbacks: {
          title: (items) => {
            if (!items.length) return '';
            const xVal = items[0].parsed.x ?? 0;
            return `Return: ${(xVal * 100).toFixed(1)}%`;
          },
          label: (item) => {
            const xVal = item.parsed.x ?? 0;
            const loss = Math.abs(exposureRef.current) * Math.max(0, -xVal);
            return loss > 0 ? `Loss: ${FormatToShortPriceTemplate(loss)}` : 'No loss';
          },
        },
      },
    },
  }), [xMin, xMax]);

  if (sigma <= 0) return null;

  return (
    <div style={{ width, height }} className="relative">
      <Line ref={chartRef} data={data} options={options} plugins={[varOverlayPlugin]} width={width} height={height} />
      <div className="absolute bottom-0 left-0 right-0 flex justify-between px-1 text-[8px] text-gray-400">
        <span>loss</span>
        <span>gain</span>
      </div>
    </div>
  );
};
